import { useState } from "react";
import { Link, router } from "@inertiajs/react";
import {
  Button,
  Card,
  Flex,
  SegmentedControl,
  Separator,
  Text,
} from "@radix-ui/themes";
import { RotateCcw } from "lucide-react";
import ColorField from "@/shared/primitives/ColorField";
import {
  BACKGROUND,
  fillFor,
  inkFor,
  normalizeAccent,
  rejectionReason,
} from "@/shared/galleryTheme";
import {
  ManagerGallery,
  ManagerGalleryAppearance,
} from "@/types/manager_gallery";

interface Props {
  gallery: ManagerGallery;
  appearance: ManagerGalleryAppearance;
  eventName: string;
  clubName: string | null;
  updatePath: string;
  /** Where a manager goes to attach a poster, linked when there isn't one. */
  eventEditPath: string;
}

interface PresetRowProps {
  presets: { name: string; hex: string }[];
  selected: string | null;
  onPick: (hex: string) => void;
}

// Presets first: most events are one click, and every colour on this row is
// contrast-checked on the server.
function PresetRow({ presets, selected, onPick }: PresetRowProps) {
  return (
    <Flex gap="2" wrap="wrap">
      {presets.map((preset) => (
        <button
          key={preset.hex}
          type="button"
          title={preset.name}
          aria-label={preset.name}
          aria-pressed={selected === preset.hex}
          onClick={() => onPick(preset.hex)}
          style={{
            width: "2rem",
            height: "2rem",
            padding: 0,
            cursor: "pointer",
            background: preset.hex,
            border:
              selected === preset.hex
                ? "2px solid var(--gray-12)"
                : "2px solid transparent",
            outline: "1px solid var(--gray-6)",
            borderTopRightRadius: ".5rem",
            borderBottomLeftRadius: ".5rem",
          }}
        />
      ))}
    </Flex>
  );
}

// The two things a manager may change about how their gallery looks: the accent
// and, optionally, a colour to blend it into. Not a panel of colours — every
// muted tone on the public page is white at some opacity over a fixed
// near-black, so these are the only values that change the page rather than
// breaking it.
export default function GalleryAppearanceForm({
  gallery,
  appearance,
  eventName,
  clubName,
  updatePath,
  eventEditPath,
}: Props) {
  const [value, setValue] = useState(gallery.accent ?? "");
  const [value2, setValue2] = useState(gallery.accent_2 ?? "");
  const [masthead, setMasthead] = useState(gallery.masthead ?? "name");
  const [saving, setSaving] = useState(false);

  const overriding = value.trim() !== "";
  const normalized = overriding ? normalizeAccent(value) : null;
  const normalized2 = value2.trim() !== "" ? normalizeAccent(value2) : null;

  // What the preview paints. An empty first field is not "no colour" — it is
  // the inherited one, and the pair is inherited together, so an empty override
  // shows the club's second colour too rather than the manager's.
  const effective = normalized ?? appearance.inherited_accent;
  const effective2 = overriding ? normalized2 : appearance.inherited_accent_2;

  // A colour blended into itself is a flat fill, which is how the server stores
  // it too — so the preview must not pretend otherwise.
  const accents =
    effective2 && effective2 !== effective
      ? [effective, effective2]
      : [effective];
  const fill = fillFor(accents);
  const ink = inkFor(accents);

  const error =
    (overriding ? rejectionReason(value) : null) ??
    (value2.trim() !== "" ? rejectionReason(value2) : null) ??
    // The model's rule, said early: with no accent of its own the gallery
    // inherits the club's pair whole, so a lone second colour would be stored
    // and then never painted.
    (value2.trim() !== "" && !overriding
      ? "Pick an accent above first — a second colour needs one to blend from."
      : null);

  // The poster is only really shown when one exists; the setting can be saved
  // ahead of the artwork, and the public page keeps showing the name until it
  // arrives. The preview has to tell that truth rather than the setting's.
  const showingPoster = masthead === "poster" && appearance.poster_url !== null;

  const dirty =
    (normalized ?? null) !== (gallery.accent ?? null) ||
    (normalized2 ?? null) !== (gallery.accent_2 ?? null) ||
    masthead !== (gallery.masthead ?? "name");

  // The trailing number renders in the accent on the real masthead, so the
  // preview splits the name the same way.
  const words = eventName.trim().split(/\s+/);
  const tail = words.length > 1 ? words.pop()! : "";
  const head = words.join(" ");

  function save() {
    setSaving(true);
    router.patch(
      updatePath,
      // Empty strings, not nulls: they clear the override and drop the keys,
      // putting the gallery back on the inherited colours.
      {
        gallery: {
          accent: normalized ?? "",
          accent_2: normalized2 ?? "",
          masthead,
        },
      },
      { preserveScroll: true, onFinish: () => setSaving(false) },
    );
  }

  return (
    <Card>
      <Flex direction="column" gap="4">
        <Flex direction="column" gap="1">
          <Text as="label" size="2" weight="medium" htmlFor="gallery-accent">
            Accent colour
          </Text>
          <Text size="1" color="gray">
            Buttons, highlights and the crop marks on the public gallery. Pick
            the colour off this event&rsquo;s poster.
          </Text>
        </Flex>

        <PresetRow
          presets={appearance.presets}
          selected={normalized}
          onPick={setValue}
        />

        <ColorField
          id="gallery-accent"
          label="Pick a custom colour"
          value={value}
          placeholder={appearance.inherited_accent}
          onChange={setValue}
          // Clearing the accent clears the pair: the gallery goes back to
          // inheriting, and the second colour has nothing left to blend from.
          onClear={() => {
            setValue("");
            setValue2("");
          }}
          clearLabel={
            <>
              <RotateCcw size={13} />
              Reset
            </>
          }
        />

        <Separator size="4" />

        <Flex direction="column" gap="1">
          <Text as="label" size="2" weight="medium" htmlFor="gallery-accent-2">
            Second colour <Text color="gray">(optional)</Text>
          </Text>
          <Text size="1" color="gray">
            Leave this blank and the accent paints flat. Set it and every filled
            surface — the Open button, the rule under the masthead, the scrubber
            — fades from the accent on the left to this on the right. Outlines
            and coloured text stay on the accent.
          </Text>
        </Flex>

        <PresetRow
          presets={appearance.presets}
          selected={normalized2}
          onPick={setValue2}
        />

        <ColorField
          id="gallery-accent-2"
          label="Pick a second colour"
          value={value2}
          placeholder={appearance.inherited_accent_2 ?? ""}
          fallback={effective}
          onChange={setValue2}
          onClear={() => setValue2("")}
        />

        {error ? (
          <Text size="1" color="red">
            {error}
          </Text>
        ) : (
          !overriding && (
            <Text size="1" color="gray">
              Using{" "}
              {appearance.inherited_source === "club" && clubName
                ? `${clubName}'s house colour${appearance.inherited_accent_2 ? "s" : ""}`
                : "the default Final Bout red"}
              . Pick a colour above to override it for this event only.
            </Text>
          )
        )}

        <Separator size="4" />

        <Flex direction="column" gap="2">
          <Flex direction="column" gap="1">
            <Text as="label" size="2" weight="medium">
              Masthead
            </Text>
            <Text size="1" color="gray">
              What tops the gallery: the event&rsquo;s name set in type, or the
              poster you already made for it.
            </Text>
          </Flex>

          <SegmentedControl.Root
            value={masthead}
            onValueChange={(v) => setMasthead(v as "name" | "poster")}
            style={{ alignSelf: "flex-start" }}
          >
            <SegmentedControl.Item value="name">Name</SegmentedControl.Item>
            {/* Offered even with no poster attached, because the manager may
                well set this before uploading the artwork — the note below
                says what will actually happen until then. */}
            <SegmentedControl.Item value="poster">Poster</SegmentedControl.Item>
          </SegmentedControl.Root>

          {masthead === "poster" && !appearance.has_poster && (
            <Text size="1" color="amber">
              This event has no poster yet, so the gallery will keep showing the
              name. <Link href={eventEditPath}>Add one on the event</Link> and
              it will appear here.
            </Text>
          )}
        </Flex>

        {/* Raw markup rather than Radix on purpose: this is a scale model of the
            public gallery, which is hand-written CSS in a HAML view and not a
            Radix surface. Making it out of Cards would make it look like the
            manager app instead of like the thing it is previewing. */}
        <Flex direction="column" gap="1">
          <Text size="1" color="gray">
            Preview
          </Text>
          <div
            style={{
              background: BACKGROUND,
              color: "#fff",
              padding: "1.5rem 1.25rem",
              overflow: "hidden",
              borderTopRightRadius: "1rem",
              borderBottomLeftRadius: "1rem",
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: 9,
                fontWeight: 500,
                letterSpacing: ".22em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,.45)",
              }}
            >
              <span
                style={{
                  display: "inline-block",
                  width: ".4rem",
                  height: ".4rem",
                  marginRight: ".5rem",
                  background: fill,
                  transform: "skewX(-12deg)",
                }}
              />
              Gallery
            </p>

            {showingPoster ? (
              <img
                src={appearance.poster_url!}
                alt={eventName}
                style={{
                  display: "block",
                  maxWidth: "8rem",
                  maxHeight: "10rem",
                  margin: ".5rem 0 0",
                  objectFit: "contain",
                  borderTopRightRadius: ".75rem",
                  borderBottomLeftRadius: ".75rem",
                }}
              />
            ) : (
              <p
                style={{
                  margin: ".5rem 0 0",
                  fontSize: "1.75rem",
                  fontWeight: 800,
                  fontStyle: "italic",
                  textTransform: "uppercase",
                  lineHeight: ".85",
                  letterSpacing: "-.02em",
                }}
              >
                {head}{" "}
                {/* The public page paints the numeral with the gradient too —
                    the one place text takes it, via background-clip. */}
                <span
                  style={
                    accents.length > 1
                      ? {
                          backgroundImage: fill,
                          WebkitBackgroundClip: "text",
                          backgroundClip: "text",
                          color: "transparent",
                        }
                      : { color: effective }
                  }
                >
                  {tail}
                </span>
              </p>
            )}

            <div
              style={{
                display: "flex",
                height: 5,
                margin: "1rem 0",
                overflow: "hidden",
                borderTopRightRadius: "1rem",
                borderBottomLeftRadius: "1rem",
              }}
            >
              <i style={{ width: "25%", background: fill }} />
              <i style={{ flex: 1, background: "rgba(255,255,255,.1)" }} />
            </div>

            <div
              style={{ display: "flex", alignItems: "center", gap: ".6rem" }}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: ".5rem",
                  padding: ".5rem .8rem",
                  background: fill,
                  color: ink,
                  fontSize: 9,
                  fontWeight: 600,
                  letterSpacing: ".16em",
                  textTransform: "uppercase",
                  borderTopRightRadius: ".75rem",
                  borderBottomLeftRadius: ".75rem",
                }}
              >
                Open gallery
                <span style={{ color: "inherit", opacity: 0.7 }}>
                  {gallery.images.length}
                </span>
              </span>

              {/* Selected and unselected filmstrip thumbnails. Outlined, so
                  they stay on the accent rather than taking the gradient. */}
              <span
                style={{
                  width: "2.25rem",
                  height: "1.5rem",
                  background: "#17171A",
                  border: `2px solid ${effective}`,
                  borderTopRightRadius: ".4rem",
                  borderBottomLeftRadius: ".4rem",
                }}
              />
              <span
                style={{
                  width: "2.25rem",
                  height: "1.5rem",
                  background: "#17171A",
                  border: "2px solid transparent",
                  opacity: 0.4,
                  borderTopRightRadius: ".4rem",
                  borderBottomLeftRadius: ".4rem",
                }}
              />
            </div>
          </div>
        </Flex>

        <Flex justify="end">
          <Button onClick={save} disabled={!dirty || saving || error !== null}>
            {saving ? "Saving…" : "Save appearance"}
          </Button>
        </Flex>
      </Flex>
    </Card>
  );
}
